import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import type { InvoiceLineItem } from "@/features/invoices/types";
import { dollarsToCents, formatCents } from "@/lib/utils";

interface LineItemStarterRowProps {
  onCreate: (item: Pick<InvoiceLineItem, "description" | "quantity" | "unitPriceCents">) => void;
}

/**
 * The empty-draft starter row: a fresh draft with no line items shows one blank
 * editable row (matching the mockup) instead of an empty-state message. It holds
 * its own local state and only materializes a real line item — via `onCreate` →
 * `addLineItem` — once the user has filled something in and moves focus out of
 * the row, so a draft never carries a phantom $0 row it didn't ask for, and
 * tabbing between fields doesn't create duplicates or interrupt typing.
 */
export function LineItemStarterRow({ onCreate }: LineItemStarterRowProps) {
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitPriceDollars, setUnitPriceDollars] = useState("");
  const createdRef = useRef(false);

  function handleRowBlur(e: React.FocusEvent<HTMLTableRowElement>) {
    // Only materialize when focus leaves the row entirely (not when tabbing
    // between this row's own fields), and only once.
    if (createdRef.current || e.currentTarget.contains(e.relatedTarget)) return;
    if (!description.trim() && !unitPriceDollars.trim() && !quantity.trim()) return;
    createdRef.current = true;
    onCreate({
      description: description.trim(),
      quantity: Number(quantity) || 1,
      unitPriceCents: dollarsToCents(unitPriceDollars),
    });
  }

  return (
    <TableRow onBlur={handleRowBlur}>
      <TableCell>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. IRS transcript analysis & tax relief report"
          className="border-transparent bg-transparent shadow-none"
        />
      </TableCell>
      <TableCell className="w-24">
        <Input
          type="number"
          min={0}
          step="any"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="1"
          className="border-transparent bg-transparent text-right shadow-none"
        />
      </TableCell>
      <TableCell className="w-32">
        <Input
          inputMode="decimal"
          value={unitPriceDollars}
          onChange={(e) => setUnitPriceDollars(e.target.value)}
          placeholder="0.00"
          className="border-transparent bg-transparent text-right shadow-none"
        />
      </TableCell>
      <TableCell className="text-muted-foreground text-right tabular-nums">{formatCents(0)}</TableCell>
      <TableCell className="w-10" />
    </TableRow>
  );
}
